import React, { useState, useEffect } from 'react';
import {
  Coins, CreditCard, User, Plus, Edit2, Trash2,
  History, ChevronLeft, ChevronRight, X, Loader2
} from 'lucide-react';

import api from '../../services/api';
import { Toast } from '../ui/Toast';
import ConfirmDialog from '../ConfirmDialog';
import { useLoader } from '../../contexts/LoaderContext';
import EmployeSelector from './shared/EmployeSelector';
import PeriodSelector from './shared/PeriodSelector';
import { useParamsCompta } from '../../contexts/ParametresComptaContext';

// ============================================================================
// AVANCES MODULE — Gestion des avances sur salaire
// ============================================================================
// Fonctionnalités :
//   - Liste des employés
//   - Création / modification / suppression d'avances
//   - Historique complet des avances par employé
//   - Contrôle plafond (ne peut pas dépasser le salaire)
// ============================================================================

const AvancesModule = () => {
  // États principaux
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [employes, setEmployes] = useState([]);
  const [selectedEmploye, setSelectedEmploye] = useState('');
  const [loadingEmployes, setLoadingEmployes] = useState(false);
  const [searchTermEmploye, setSearchTermEmploye] = useState('');
  const [toast, setToast] = useState({ show: false, message: '', type: '' });

  // Avances du mois
  const [avancesMois, setAvancesMois] = useState([]);
  const [totalAvancesMois, setTotalAvancesMois] = useState(0);

  // Historique
  const [historiqueAvances, setHistoriqueAvances] = useState([]);
  const [currentPageHistorique, setCurrentPageHistorique] = useState(1);
  const [itemsPerPageHistorique] = useState(3);

  // Modales création
  const [showCreateAvanceModal, setShowCreateAvanceModal] = useState(false);
  const [montantAvance, setMontantAvance] = useState('');
  const [motifAvance, setMotifAvance] = useState('');
  const [isCreatingAvance, setIsCreatingAvance] = useState(false);

  // Modales édition
  const [showEditAvanceModal, setShowEditAvanceModal] = useState(false);
  const [avanceToEdit, setAvanceToEdit] = useState(null);
  const [montantEdit, setMontantEdit] = useState('');
  const [motifEdit, setMotifEdit] = useState('');
  const [justificationEdit, setJustificationEdit] = useState('');
  const [isEditingAvance, setIsEditingAvance] = useState(false);

  // Suppression
  const [confirmDialogConfig, setConfirmDialogConfig] = useState(null);

  const { showLoader, hideLoader } = useLoader();
  const { formatMontant, symbole } = useParamsCompta();

  // Toast
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // ============================================================================
  // CHARGEMENT
  // ============================================================================
  useEffect(() => {
    loadEmployesData();
  }, [selectedDate]);

  useEffect(() => {
    if (selectedEmploye) {
      loadAvancesMois(selectedEmploye);
      loadHistoriqueAvances(selectedEmploye);
      setCurrentPageHistorique(1);
    }
  }, [selectedEmploye, selectedDate]);

  const loadEmployesData = async () => {
    showLoader();
    setLoadingEmployes(true);
    try {
      const mois = selectedDate.getMonth() + 1;
      const annee = selectedDate.getFullYear();
      const response = await api.comptabilite.getPointagesMensuelsEmployes(mois, annee);

      if (response.success) {
        setEmployes(response.data);
      } else {
        showToast(response.message || 'Erreur lors du chargement', 'error');
      }
    } catch (error) {
      console.error('Erreur chargement employés:', error);
      showToast('Erreur lors du chargement des employés', 'error');
    } finally {
      setLoadingEmployes(false);
      hideLoader();
    }
  };

  const loadAvancesMois = async (employeId) => {
    try {
      const mois = selectedDate.getMonth() + 1;
      const annee = selectedDate.getFullYear();
      const response = await api.avancesSalaire.getAvancesMoisEmploye(employeId, mois, annee);

      if (response.success) {
        setAvancesMois(response.data.avances || []);
        setTotalAvancesMois(response.data.total_avances || 0);
      }
    } catch (error) {
      console.error('Erreur chargement avances:', error);
    }
  };

  const loadHistoriqueAvances = async (employeId) => {
    try {
      const response = await api.avancesSalaire.getHistoriqueAvances(employeId);
      if (response.success) {
        setHistoriqueAvances(response.data);
      }
    } catch (error) {
      console.error('Erreur chargement historique:', error);
    }
  };

  // ============================================================================
  // HELPERS
  // ============================================================================
  const getSelectedEmployeData = () => employes.find(e => e.id.toString() === selectedEmploye);

  const getSalaireReference = () => {
    const employe = getSelectedEmployeData();
    return employe ? parseFloat(employe.salaire_fixe || 0) : 0;
  };

  const getDisponible = () => getSalaireReference() - totalAvancesMois;

  // ============================================================================
  // CRÉATION AVANCE
  // ============================================================================
  const handleCreateAvance = async () => {
    if (!montantAvance || parseFloat(montantAvance) <= 0) {
      showToast('Veuillez saisir un montant valide', 'error');
      return;
    }

    const montant = parseFloat(montantAvance);
    if (montant > getDisponible()) {
      showToast(`Montant trop élevé. Maximum : ${formatMontant(getDisponible())}`);
      return;
    }

    setIsCreatingAvance(true);
    try {
      const response = await api.avancesSalaire.creerAvanceEmploye({
        employe_id: parseInt(selectedEmploye),
        mois: selectedDate.getMonth() + 1,
        annee: selectedDate.getFullYear(),
        montant,
        motif: motifAvance || null,
      });

      if (response.success) {
        showToast('Avance créée avec succès', 'success');
        setShowCreateAvanceModal(false);
        setMontantAvance('');
        setMotifAvance('');
        loadAvancesMois(selectedEmploye);
        loadHistoriqueAvances(selectedEmploye);
        loadEmployesData();
      } else {
        showToast(response.message || 'Erreur lors de la création', 'error');
      }

  } catch (error) {
      console.error('Erreur création avance:', error);
      const msg = error.response?.data?.message || error.message || 'Erreur lors de la création';
      showToast(msg, 'error');
    } finally {

      setIsCreatingAvance(false);
    }
  };

  // ============================================================================
  // MODIFICATION AVANCE
  // ============================================================================
  const handleEditAvance = async () => {
    if (!justificationEdit.trim()) {
      showToast('Veuillez justifier la modification', 'error');
      return;
    }
    if (!montantEdit || parseFloat(montantEdit) <= 0) {
      showToast('Veuillez saisir un montant valide', 'error');
      return;
    }

    setIsEditingAvance(true);
    try {
      const response = await api.avancesSalaire.modifierAvance(avanceToEdit.id, {
        montant: parseFloat(montantEdit),
        motif: motifEdit,
        justification_modification: justificationEdit,
      });

      if (response.success) {
        showToast('Avance modifiée avec succès', 'success');
        setShowEditAvanceModal(false);
        loadAvancesMois(selectedEmploye);
        loadHistoriqueAvances(selectedEmploye);
        loadEmployesData();
      } else {
        showToast(response.message || 'Erreur', 'error');
      }

   } catch (error) {
      console.error('Erreur modification:', error);
      const msg = error.response?.data?.message || error.message || 'Erreur lors de la modification';
      showToast(msg, 'error');
    } finally {

      setIsEditingAvance(false);
    }
  };

  // Données employé sélectionné
  const employeActuel = getSelectedEmployeData();

  // Pagination historique
  const indexOfLastItem = currentPageHistorique * itemsPerPageHistorique;
  const indexOfFirstItem = indexOfLastItem - itemsPerPageHistorique;
  const currentHistoriqueItems = historiqueAvances.slice(indexOfFirstItem, indexOfLastItem);
  const totalPagesHistorique = Math.ceil(historiqueAvances.length / itemsPerPageHistorique);

  // ============================================================================
  // RENDU
  // ============================================================================
  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: '#fdfcf5' }}>
      {/* Sélecteur de période */}
      <PeriodSelector selectedDate={selectedDate} onChange={setSelectedDate} />

      {/* Grille principale */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Colonne 1 : Liste employés */}
        <div className="lg:col-span-3">
          <EmployeSelector
            employes={employes}
            selectedEmploye={selectedEmploye}
            onSelect={(emp) => setSelectedEmploye(emp.id.toString())}
            searchTerm={searchTermEmploye}
            onSearchChange={setSearchTermEmploye}
            loading={loadingEmployes}
            color="accent"
          />
        </div>

        {/* Colonne 2 : Gestion des avances */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:col-span-6">
          <div className="px-6 py-4 mb-5 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#fed774' }}>
            <Coins size={24} className="text-gray-900" />
            <h2 className="font-bold text-gray-900 text-lg">Gestion des avances</h2>
          </div>

          {!selectedEmploye ? (
            <div className="text-center py-16 text-gray-400">
              <CreditCard className="h-20 w-20 mx-auto mb-4 opacity-50" />
              <h4 className="text-lg font-bold text-gray-700 mb-2">Aucun employé sélectionné</h4>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Info employé */}
              <div className="p-4 rounded-xl border-2" style={{ backgroundColor: '#fff5e5', borderColor: '#fed774' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-bold text-gray-900">{employeActuel?.prenom} {employeActuel?.nom}</div>
                    <div className="text-sm font-medium text-gray-600 mt-1">{employeActuel?.role}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-black">{formatMontant(getSalaireReference())}</div>
                    <div className="text-xs font-medium text-gray-600">Salaire base</div>
                  </div>
                </div>
              </div>

              {/* Bouton créer */}
              <button
                onClick={() => { setMontantAvance(''); setMotifAvance(''); setShowCreateAvanceModal(true); }}
                className="w-full py-3 px-4 text-gray-900 rounded-xl font-semibold text-sm transition-all shadow-lg hover:shadow-xl"
                style={{ backgroundColor: '#fed774' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fdd24e'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#fed774'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <Plus className="w-4 h-4 inline mr-2" />
                Nouvelle avance
              </button>

              {/* Liste avances du mois */}
              <div className="rounded-xl border border-gray-100 overflow-hidden">
                {avancesMois.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">
                    <CreditCard className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="text-sm font-medium">Aucune avance ce mois</p>
                  </div>
                ) : (
                  <>
                    {avancesMois.map((avance) => (
                      <div key={avance.id} className="p-4 border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xl font-bold" style={{ color: '#fed774' }}>
                            {formatMontant(avance.montant)}
                          </div>
                          <span className={`text-xs px-3 py-1 font-semibold rounded-lg ${
                            avance.statut === 'validee' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {avance.statut === 'validee' ? 'Validée' : 'Appliquée'}
                          </span>
                        </div>

                        {avance.motif && (
                          <div className="text-sm font-medium text-gray-700 mb-2">{avance.motif}</div>
                        )}

                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <div className="text-xs font-medium text-gray-600">
                              {new Date(avance.date_demande).toLocaleDateString('fr-FR')}
                            </div>
                            {avance.created_by_name && (
                              <div className="flex items-center gap-1 text-xs font-medium text-gray-500">
                                <User size={12} />
                                <span>Par : {avance.created_by_name}</span>
                              </div>
                            )}
                          </div>

                          {avance.statut === 'validee' && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  setAvanceToEdit(avance);
                                  setMontantEdit(avance.montant.toString());
                                  setMotifEdit(avance.motif || '');
                                  setJustificationEdit('');
                                  setShowEditAvanceModal(true);
                                }}
                                className="p-2 rounded-lg hover:bg-blue-50 transition-colors"
                              >
                                <Edit2 className="w-4 h-4" style={{ color: '#448696' }} />
                              </button>
                              <button
                                onClick={() => setConfirmDialogConfig({ avanceId: avance.id, montant: avance.montant })}
                                className="p-2 rounded-lg hover:bg-red-50 transition-colors"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Total */}
                    <div className="p-4" style={{ backgroundColor: '#f5f3eb' }}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold text-gray-700">Total</span>
                        <span className="text-2xl font-bold" style={{ color: '#000000' }}>
                          {formatMontant(totalAvancesMois)}
                        </span>
                      </div>
                      <div className="mt-2 text-xs font-medium text-gray-600">
                        Disponible : {formatMontant(getDisponible())}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Colonne 3 : Historique */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 lg:col-span-3">
          <div className="px-6 py-4 mb-5 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#448696' }}>
            <History size={24} className="text-white" />
            <h2 className="font-bold text-white text-lg">Historique</h2>
          </div>

          {!selectedEmploye ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-xs font-medium">Sélectionnez un employé</p>
            </div>
          ) : historiqueAvances.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <p className="text-xs font-medium">Aucune avance</p>
            </div>
          ) : (
            <>
              <div className="space-y-3 mb-4">
                {currentHistoriqueItems.map((avance) => (
                  <div
                    key={avance.id}
                    className={`p-3 rounded-xl border transition-colors ${
                      avance.statut === 'annulee' ? 'border-red-200 bg-red-50 opacity-70' : 'border-gray-100 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold text-gray-900">{avance.mois}/{avance.annee}</div>
                      <span className={`text-xs px-2 py-1 font-semibold rounded-lg ${
                        avance.statut === 'validee' ? 'bg-green-100 text-green-700'
                          : avance.statut === 'appliquee' ? 'bg-blue-100 text-blue-700'
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {avance.statut === 'validee' ? 'Validée' : avance.statut === 'appliquee' ? 'Appliquée' : 'Annulée'}
                      </span>
                    </div>

                    <div className={`text-lg font-bold mb-2 ${avance.statut === 'annulee' ? 'line-through' : ''}`}
                      style={{ color: avance.statut === 'annulee' ? '#9ca3af' : '#fed774' }}>
                      {formatMontant(avance.montant)}
                    </div>

                    <div className="space-y-1">
                      <div className="text-xs font-medium text-gray-600">
                        Créée le {new Date(avance.date_demande).toLocaleDateString('fr-FR')}
                      </div>
                      {avance.created_by_name && (
                        <div className="flex items-center gap-1 text-xs font-medium text-gray-500">
                          <User size={12} /><span>Par : {avance.created_by_name}</span>
                        </div>
                      )}
                      {avance.statut === 'annulee' && (
                        <>
                          {avance.date_suppression && (
                            <div className="flex items-center gap-1 text-xs font-medium text-red-600 mt-2">
                              <X size={12} /><span>Annulée le {new Date(avance.date_suppression).toLocaleDateString('fr-FR')}</span>
                            </div>
                          )}
                          {avance.supprime_par_name && (
                            <div className="flex items-center gap-1 text-xs font-medium text-red-600">
                              <User size={12} /><span>Par : {avance.supprime_par_name}</span>
                            </div>
                          )}
                          {avance.justification_suppression && (
                            <div className="text-xs italic text-red-700 mt-2 p-2 bg-red-100 rounded border border-red-200">
                              "{avance.justification_suppression}"
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPagesHistorique > 1 && (
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <div className="text-xs font-medium text-gray-600">Page {currentPageHistorique} sur {totalPagesHistorique}</div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPageHistorique(prev => Math.max(prev - 1, 1))}
                      disabled={currentPageHistorique === 1}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => setCurrentPageHistorique(prev => Math.min(prev + 1, totalPagesHistorique))}
                      disabled={currentPageHistorique === totalPagesHistorique}
                      className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ================================================================== */}
      {/* MODALES                                                           */}
      {/* ================================================================== */}

      {/* Modal Création */}
      {showCreateAvanceModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="px-6 py-4 mb-6 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#fed774' }}>
              <Plus size={24} className="text-gray-900" />
              <h3 className="text-xl font-bold text-gray-900">Nouvelle avance</h3>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">{`Montant (${symbole}) *`}</label>
              <input
                type="number" value={montantAvance} onChange={(e) => setMontantAvance(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#fed774'; e.target.style.boxShadow = '0 0 0 3px rgba(254, 215, 116, 0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }}
              />
              <div className="mt-1 text-xs font-medium text-gray-600">Max : {formatMontant(getDisponible())}</div>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Motif</label>
              <textarea
                value={motifAvance} onChange={(e) => setMotifAvance(e.target.value)} rows="3"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#fed774'; e.target.style.boxShadow = '0 0 0 3px rgba(254, 215, 116, 0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowCreateAvanceModal(false)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">Annuler</button>
              <button onClick={handleCreateAvance} disabled={isCreatingAvance} className="flex-1 px-4 py-3 text-gray-900 rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all disabled:opacity-50" style={{ backgroundColor: '#fed774' }}
                onMouseEnter={(e) => !isCreatingAvance && (e.currentTarget.style.backgroundColor = '#fdd24e')}
                onMouseLeave={(e) => !isCreatingAvance && (e.currentTarget.style.backgroundColor = '#fed774')}>
                {isCreatingAvance ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Édition */}
      {showEditAvanceModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
            <div className="px-6 py-4 mb-6 rounded-xl flex items-center gap-3" style={{ backgroundColor: '#fed774' }}>
              <Edit2 size={24} className="text-gray-900" />
              <h3 className="text-xl font-bold text-gray-900">Modifier avance</h3>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Montant *</label>
              <input type="number" value={montantEdit} onChange={(e) => setMontantEdit(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#fed774'; e.target.style.boxShadow = '0 0 0 3px rgba(254, 215, 116, 0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Motif</label>
              <textarea value={motifEdit} onChange={(e) => setMotifEdit(e.target.value)} rows="2"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#fed774'; e.target.style.boxShadow = '0 0 0 3px rgba(254, 215, 116, 0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Justification *</label>
              <textarea value={justificationEdit} onChange={(e) => setJustificationEdit(e.target.value)} rows="3"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl font-medium focus:outline-none transition-all"
                onFocus={(e) => { e.target.style.borderColor = '#fed774'; e.target.style.boxShadow = '0 0 0 3px rgba(254, 215, 116, 0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e5e5e5'; e.target.style.boxShadow = 'none'; }}
              />
            </div>

            <div className="flex gap-3">
              <button onClick={() => setShowEditAvanceModal(false)} className="flex-1 px-4 py-3 rounded-xl font-semibold text-sm bg-white border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all">Annuler</button>
              <button onClick={handleEditAvance} disabled={isEditingAvance} className="flex-1 px-4 py-3 text-gray-900 rounded-xl font-semibold text-sm shadow-lg hover:shadow-xl transition-all disabled:opacity-50" style={{ backgroundColor: '#fed774' }}
                onMouseEnter={(e) => !isEditingAvance && (e.currentTarget.style.backgroundColor = '#fdd24e')}
                onMouseLeave={(e) => !isEditingAvance && (e.currentTarget.style.backgroundColor = '#fed774')}>
                {isEditingAvance ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Modifier'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ConfirmDialog suppression */}
      {confirmDialogConfig && (
        <ConfirmDialog
          isOpen={true}
          title="Annuler cette avance"
          message={`Confirmer l'annulation de l'avance de ${formatMontant(confirmDialogConfig.montant)} ?`}
          type="warning"
          confirmText="Annuler l'avance"
          cancelText="Retour"
          requireJustification={false}
          onConfirm={async () => {
            try {
              const response = await api.avancesSalaire.supprimerAvance(confirmDialogConfig.avanceId, null);
              if (response.success) {
                showToast('Avance annulée avec succès', 'success');
                await loadAvancesMois(selectedEmploye);
                await loadHistoriqueAvances(selectedEmploye);
                await loadEmployesData();
                setConfirmDialogConfig(null);
                return true;
              } else {
                showToast(response.message || 'Erreur', 'error');
                return false;
              }

          } catch (error) {
              console.error('Erreur suppression:', error);
              const msg = error.response?.data?.message || error.message || 'Erreur lors de la suppression';
              showToast(msg, 'error');
              return false;
            }

          }}
          onCancel={() => setConfirmDialogConfig(null)}
        />
      )}

      {/* Toast */}
      {toast.show && (
        <Toast show={toast.show} message={toast.message} type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: '' })} />
      )}
    </div>
  );
};

export default AvancesModule;